/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import { addClassNamesToElement } from "@lexical/utils";
import { isContractValidationPage, isSetupPage } from "helpers/utils";
import type {
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
} from "lexical";
import { DOMConversionMap, ElementNode } from "lexical";

type SerializedConditionalContainerNode = Spread<
    {
        open: boolean;
        show: boolean;
    },
    SerializedElementNode
>;

export function convertDetailsElement(domNode: HTMLDetailsElement): DOMConversionOutput | null {
    const isOpen = domNode.open !== undefined ? domNode.open : true;
    // const index = domNode.index !== undefined ? domNode.index : 0;
    const node = $createConditionalContainerNode(isOpen, true);
    return {
        node,
    };
}

export class ConditionalContainerNode extends ElementNode {
    __open: boolean;
    __show: boolean = true;

    getShow(): boolean {
        const self = this.getLatest();
        return self.__show;
    }

    toggleShow(): void {
        this.setShow(!this.getShow());
    }

    setShow(value: boolean) {
        const self = this.getWritable();
        self.__show = value;
    }

    constructor(open: boolean, show: boolean, key?: NodeKey) {
        super(key);
        this.__open = open;
        this.__show = show;
    }

    static getType(): string {
        return "conditional-container";
    }

    static clone(node: ConditionalContainerNode): ConditionalContainerNode {
        return new ConditionalContainerNode(node.__open, node.__show, node.__key);
    }

    createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
        const dom = document.createElement("details");
        dom.open = this.__open;
        dom.classList.add("Conditional__container");
        const theme = config.theme;
        const className = theme.conditional;
        if (className !== undefined && !isContractValidationPage()) {
            addClassNamesToElement(dom, className);
        }
        // uncomment if we want to make the conditionnal container collapsible
        // dom.addEventListener("toggle", () => {
        //     const open = editor.getEditorState().read(() => this.getOpen());
        //     if (open !== dom.open) {
        //         editor.update(() => this.toggleOpen());
        //     }
        // });
        dom.classList.toggle("show", this.__show);
        if (isContractValidationPage()) addClassNamesToElement(dom, "contract-validation-page");
        if (isSetupPage()) {
            const checkbox = dom.appendChild(document.createElement("input"));
            checkbox.type = "checkbox";
            checkbox.checked = this.getShow();
            addClassNamesToElement(checkbox, theme.conditionalCheckbox);
            checkbox.addEventListener("change", (e) => {
                const show = editor.getEditorState().read(() => this.getShow());
                if (show !== checkbox.checked) {
                    editor.update(() => {
                        this.toggleShow();
                    });
                }
            });
        }
        return dom;
    }

    updateDOM(prevNode: ConditionalContainerNode, dom: HTMLDetailsElement): boolean {
        if (prevNode.__open !== this.__open) {
            dom.open = this.__open;
        }
        if (prevNode.__show !== this.__show) {
            (dom.firstChild as HTMLInputElement).checked = this.__show;
            dom.classList.toggle("show", this.__show);
        }

        return false;
    }

    static importJSON(serializedNode: SerializedConditionalContainerNode): ConditionalContainerNode {
        const node = $createConditionalContainerNode(serializedNode.open, serializedNode.show);
        return node;
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput {
        const { element } = super.exportDOM(editor);
        if (!this.__show) {
            return { element: null };
        }
        return { element };
    }

    exportJSON(): SerializedConditionalContainerNode {
        return {
            ...super.exportJSON(),
            open: this.__open,
            show: this.__show,
            type: "conditional-container",
            version: 1,
        };
    }

    setOpen(open: boolean): void {
        const writable = this.getWritable();
        writable.__open = open;
    }

    getOpen(): boolean {
        return this.getLatest().__open;
    }

    toggleOpen(): void {
        this.setOpen(!this.getOpen());
    }
}

export function $createConditionalContainerNode(isOpen: boolean, show: boolean): ConditionalContainerNode {
    return new ConditionalContainerNode(isOpen, show);
}

export function $isConditionalContainerNode(node: LexicalNode | null | undefined): node is ConditionalContainerNode {
    return node instanceof ConditionalContainerNode;
}
